'use client';

import { PropsWithChildren } from 'react';

import posthog from 'posthog-js';

import { IS_TRACKING_ENABLED } from '@/common/constants';
import { SafePostHogProvider } from '@/hooks/use-safe-posthog';

/**
 * Bridges the PostHog singleton into React context via SafePostHogProvider.
 *
 * IMPORTANT: This component MUST always render `<SafePostHogProvider>` wrapping
 * children — even when the value is `null` (before the client-side effect runs).
 *
 * A previous implementation conditionally returned bare `<>{children}</>` on the
 * first render (`if (!hasMounted) return <>{children}</>`), which meant
 * `SafePostHogProvider` was absent from the tree during the initial mount.
 * Consequently, `WidgetAnalyticsProvider.useLayoutEffect` saw `posthog = null`
 * (no provider above it) and `posthog?.register()` was a permanent no-op — super
 * properties like `tenantKey` were never set for those sessions.
 *
 * The current pattern (value starts as `null`, then transitions to the PostHog
 * instance via `useEffect`) keeps the provider in the tree from the very first
 * render. When the value changes, consumers re-render and effects re-run.
 */
export function PostHogProvider({ children }: PropsWithChildren) {
  return (
    <SafePostHogProvider value={IS_TRACKING_ENABLED ? posthog : null}>
      {children}
    </SafePostHogProvider>
  );
}
